import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import { Spinner } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { setUiLocale } from "../redux/reducers/persistent/preferences";
import {
    hasTranslationResource,
    i18n,
    i18nReady,
    retryI18nInitialization,
    type TranslationRuntimeConfig,
} from "./i18next";
import {
    getBrowserLocales,
    resolveApplicationLocale,
} from "./applicationLocale";
import { ApplicationLocaleProvider } from "./ApplicationLocaleContext";
import { TranslationNamespace } from "../data/models/schemas";

type ApplicationLocaleBoundaryProps = {
    children: ReactNode;
};

const ApplicationLocaleBoundary = ({
    children,
}: ApplicationLocaleBoundaryProps) => {
    const dispatch = useAppDispatch();
    const savedLocale = useAppSelector(
        (state) => state.persistent?.preferences?.uiLocale
    );
    const browserLocales = useMemo(() => getBrowserLocales(), []);
    const [runtimeConfig, setRuntimeConfig] =
        useState<TranslationRuntimeConfig | null>(null);
    const [appliedLocale, setAppliedLocale] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [retryRevision, setRetryRevision] = useState(0);
    const targetLocale = runtimeConfig
        ? resolveApplicationLocale(
              savedLocale,
              browserLocales,
              runtimeConfig.applicationLocales.map((locale) => locale.value)
          )
        : null;

    useEffect(() => {
        let active = true;

        void (async () => {
            try {
                setLoadError(null);
                const config =
                    retryRevision === 0
                        ? await i18nReady
                        : await retryI18nInitialization();
                if (!active) return;
                setRuntimeConfig(config);
                const nextLocale = resolveApplicationLocale(
                    savedLocale,
                    browserLocales,
                    config.applicationLocales.map((locale) => locale.value)
                );
                if (savedLocale !== nextLocale) {
                    dispatch(setUiLocale(nextLocale));
                }
                await i18n.changeLanguage(nextLocale);
                if (
                    !hasTranslationResource(
                        nextLocale,
                        TranslationNamespace.TRANSLATION
                    )
                ) {
                    throw new Error(
                        "Application translation catalog is empty."
                    );
                }
                if (!active) return;
                document.documentElement.lang = nextLocale;
                setAppliedLocale(nextLocale);
            } catch (error) {
                if (!active) return;
                setAppliedLocale(null);
                setLoadError(
                    error instanceof Error
                        ? error.message
                        : "Translations could not be loaded."
                );
            }
        })();

        return () => {
            active = false;
        };
    }, [browserLocales, dispatch, retryRevision, savedLocale]);

    if (loadError) {
        return (
            <div
                className="d-flex min-vh-100 flex-column align-items-center justify-content-center gap-3 p-4 text-center"
                role="alert"
            >
                <div>Translations could not be loaded.</div>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setRetryRevision((revision) => revision + 1)}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!runtimeConfig || !targetLocale || appliedLocale !== targetLocale) {
        return (
            <div
                className="d-flex min-vh-100 align-items-center justify-content-center"
                aria-busy="true"
                aria-live="polite"
            >
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <ApplicationLocaleProvider options={runtimeConfig.applicationLocales}>
            <React.Fragment key={appliedLocale}>{children}</React.Fragment>
        </ApplicationLocaleProvider>
    );
};

export default ApplicationLocaleBoundary;
